import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { policyService } from '../api';
import type { PolicyViewModel } from '../api/types/policy';
import { policyRoutePath } from '../utils/policyRouteSlug';
import { SOCIAL_MEDIA_ENTRIES } from '../config/socialMedia';
import './Footer.css';

const DEFAULT_ETBIS_LINK =
  'https://etbis.ticaret.gov.tr/tr/SiteSorgulamaSonuc?siteId=24ef86c1-f266-40ac-8039-a08ed8627b20';

const Footer: React.FC = () => {
  const [policies, setPolicies] = useState<PolicyViewModel[]>([]);
  const etbisLogoUrl = process.env.REACT_APP_ETBIS_LOGO_URL?.trim() || '/etbis-logo.png';
  const guvenDamgasiLogoUrl = process.env.REACT_APP_GUVEN_DAMGA_LOGO_URL?.trim() || '';
  const etbisLinkUrl =
    process.env.REACT_APP_ETBIS_LINK_URL?.trim() || DEFAULT_ETBIS_LINK;
  const guvenDamgasiLinkUrl = process.env.REACT_APP_GUVEN_DAMGA_LINK_URL?.trim();
  const hasTrustBadges = Boolean(etbisLogoUrl || guvenDamgasiLogoUrl);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const result = await policyService.getActivePolicies();
        const list = result.data;
        setPolicies(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching policies:', error);
        // Silently fail - footer will show without dynamic policies
      }
    };

    fetchPolicies();
  }, []);

  return (
    <footer className="footer sb-site-footer mt-auto">
      <div className="container sb-site-footer__inner">
        <div className="row g-4 sb-site-footer__columns">
          <div className="col-12 col-md-6 col-lg-3 text-center text-md-start">
            <h5 className="sb-site-footer__heading mb-3">Hakkımızda</h5>
            <p className="text-secondary">
              SadeceBilgisayar.com, sıfır veya İkinci el teknolojik ürünleri güvenilir bir şekilde alım veya satım yapabileceğiniz bir platformdur.
            </p>
          </div>
          <div className="col-12 col-md-6 col-lg-3 text-center text-md-start">
            <h5 className="sb-site-footer__heading mb-3">Önemli Tavsiyeler</h5>
            <ul className="list-unstyled">
              <li className="mb-2">
                <Link className="text-secondary text-decoration-none sb-footer-advice-link" to="/masaustu_tavsiyeler">
                  Masaüstü alırken dikkat edilmesi gerekenler
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-secondary text-decoration-none sb-footer-advice-link" to="/laptop-tavsiyeler">
                  Laptop alırken dikkat edilmesi gerekenler
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-secondary text-decoration-none sb-footer-advice-link" to="/donanim-tavsiyeler">
                  Donanım alırken dikkat edilmesi gerekenler
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-12 col-md-6 col-lg-3 text-center text-md-start">
            <h5 className="sb-site-footer__heading mb-3">İletişim</h5>
            <p className="sb-site-footer__text mb-0">
              İletişim için{' '}
              <Link className="sb-site-footer__inline-link" to="/teknik-destek">
                Teknik Destek
              </Link>{' '}
              üzerinden bize ulaşabilirsiniz.
            </p>
          </div>
          <div className="col-12 col-md-6 col-lg-3 text-center text-md-start">
            <div className="d-inline-block">
              <h5 className="sb-site-footer__heading mb-3">Sosyal Medya Hesaplarımız</h5>
              <ul className="sb-footer-social mt-2 mb-0">
                {SOCIAL_MEDIA_ENTRIES.map((entry) => {
                  const hasUrl = Boolean(entry.url?.trim());
                  const label = `${entry.label}: ${entry.username}`;
                  return (
                    <li key={entry.id}>
                      {hasUrl ? (
                        <a
                          className={`sb-footer-social__link ${entry.linkClass}`}
                          href={entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          title={entry.username}
                        >
                          <i className={entry.iconClass} aria-hidden />
                        </a>
                      ) : (
                        <span className="sb-footer-social__text" aria-label={label}>
                          <i className={entry.iconClass} aria-hidden />
                          {entry.username}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
        <div className="footer-bottom sb-site-footer__bottom mt-5 pt-4">
          {hasTrustBadges ? (
            <div className="row align-items-center g-3 mb-4">
              <div className="col-12 text-center">
                <div className="d-flex flex-wrap justify-content-center align-items-center gap-4">
                  {etbisLogoUrl ? (
                    etbisLinkUrl ? (
                      <a href={etbisLinkUrl} target="_blank" rel="noopener noreferrer">
                        <img src={etbisLogoUrl} alt="ETBIS" height={48} />
                      </a>
                    ) : (
                      <img src={etbisLogoUrl} alt="ETBIS" height={48} />
                    )
                  ) : null}
                  {guvenDamgasiLogoUrl ? (
                    guvenDamgasiLinkUrl ? (
                      <a href={guvenDamgasiLinkUrl} target="_blank" rel="noopener noreferrer">
                        <img src={guvenDamgasiLogoUrl} alt="Güven Damgası" height={48} />
                      </a>
                    ) : (
                      <img src={guvenDamgasiLogoUrl} alt="Güven Damgası" height={48} />
                    )
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          <div className="row align-items-center g-2">
            <div className="col-12 col-md-6 text-center text-md-start">
              <p className="sb-site-footer__copyright mb-0">© {new Date().getFullYear()} SadeceBilgisayar.com — Tüm hakları saklıdır.</p>
            </div>
            <div className="col-12 col-md-6 text-center text-md-end">
              {policies.length > 0 ? (
                policies.map((policy) => (
                  <Link
                    key={policy.id}
                    className="sb-site-footer__policy-link me-3"
                    to={policyRoutePath(policy.slug, policy.name)}
                  >
                    {policy.name}
                  </Link>
                ))
              ) : (
                <>
                  <Link className="sb-site-footer__policy-link me-3" to="/gizlilik-politikasi">Gizlilik Politikası</Link>
                  <Link className="sb-site-footer__policy-link me-3" to="/kullanici-sozlesmesi">Kullanım Koşulları</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 